import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import { ArtistCreate, ArtistUpdate } from "@api/artists";
import { generateSlug } from "@lib/utils";
import { t } from "@lib/i18n";

const artistSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
  slug: z.string().max(255, "Slug is too long").optional(),
});

type ArtistFormData = z.infer<typeof artistSchema>;

interface ArtistFormProps {
  initialData?: { name: string; slug: string };
  onSubmit: (data: ArtistCreate | ArtistUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ArtistForm({ initialData, onSubmit, onCancel, isLoading }: ArtistFormProps) {
  const form = useForm<ArtistFormData>({
    resolver: zodResolver(artistSchema),
    defaultValues: {
      name: initialData?.name || "",
      slug: initialData?.slug || "",
    },
  });

  const handleNameChange = (value: string) => {
    form.setValue("name", value);
    // Auto-generate slug if slug is empty or matches the old name
    const currentSlug = form.getValues("slug");
    if (!currentSlug || currentSlug === generateSlug(initialData?.name || "")) {
      form.setValue("slug", generateSlug(value));
    }
  };

  const handleSubmit = (data: ArtistFormData) => {
    if (initialData) {
      // Update mode - only send changed fields
      const update: ArtistUpdate = {};
      if (data.name !== initialData.name) update.name = data.name;
      if (data.slug !== initialData.slug) update.slug = data.slug;
      onSubmit(update);
    } else {
      // Create mode
      onSubmit({
        name: data.name,
        slug: data.slug || undefined,
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("artists.nameLabel")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("artists.namePlaceholder")}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleNameChange(e.target.value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("artists.slugLabel")}</FormLabel>
              <FormControl>
                <Input placeholder={t("artists.slugPlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("common.saving") : initialData ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

